import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faWallet, faHistory, faMoneyBillWave, faExchangeAlt,
    faCheckCircle, faExclamationTriangle, faSpinner, faTimes,
    faArrowUp, faArrowDown, faCalendarAlt, faBuilding,
    faUser, faChartLine, faPrint, faSearch, faFilter,
    faPercent
} from '@fortawesome/free-solid-svg-icons';

// Toast Notification Component
const ToastNotification = ({ toast, setToast }) => {
    if (!toast.show) return null;
    return (
        <div className={`toast-notification ${toast.type}`}>
            <div className="toast-content">
                <div className="toast-icon">
                    <FontAwesomeIcon icon={toast.type === 'success' ? faCheckCircle : faExclamationTriangle} />
                </div>
                <div className="toast-text">
                    <div className="toast-message">{toast.message}</div>
                </div>
                <button className="toast-close" onClick={() => setToast({ show: false })}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

const OwnerAccount = () => {
    const { user, currentShop } = useAuth();
    
    const [balance, setBalance] = useState({
        total_balance: 0,
        total_commission_earned: 0,
        total_partner_transfers: 0,
        currency: 'USDT'
    });
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [toast, setToast] = useState({ show: false, type: '', message: '' });
    const [filterType, setFilterType] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });

    const showToast = (type, message) => {
        setToast({ show: true, type, message });
        setTimeout(() => setToast({ show: false }), 3000);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', { 
            minimumFractionDigits: 2, 
            maximumFractionDigits: 6 
        }).format(amount || 0);
    };

    const formatDate = (dateString) => {
        return dateString ? new Date(dateString).toLocaleDateString('en-IN') : '';
    };

    const loadBalance = useCallback(async () => {
        if (!currentShop) return;
        
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/others_account.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_owner_account_balance',
                    shop_id: currentShop.shop_id,
                    username: user?.username,
                    currency: 'USDT'
                })
            });
            
            const data = await response.json();
            if (data.status === 'success') {
                setBalance(data.data);
            }
        } catch (error) {
            console.error('Error loading balance:', error);
            showToast('error', 'Failed to load balance');
        }
    }, [currentShop, user?.username]);

    const loadTransactions = useCallback(async () => {
        if (!currentShop) return;
        
        setLoading(true);
        try {
            // Use the new get_owner_transactions action that queries owner_transactions table
            const response = await fetch('https://ourzestapp.online/oz_backend/others_account.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_owner_transactions',
                    shop_id: currentShop.shop_id,
                    username: user?.username,
                    limit: 200,
                    source_type: filterType === 'all' ? null : filterType
                })
            });
            
            const data = await response.json();
            if (data.status === 'success') {
                setTransactions(data.data || []);
            } else {
                showToast('error', data.message || 'Failed to load transactions');
            }
        } catch (error) {
            console.error('Error loading transactions:', error);
            showToast('error', 'Network error');
        } finally {
            setLoading(false);
        }
    }, [currentShop, user?.username, filterType]);

    // Also load from owner_account for balance calculation
    useEffect(() => {
        if (currentShop && user?.username) {
            loadBalance();
            loadTransactions();
        }
    }, [currentShop, user?.username, loadBalance, loadTransactions, filterType]);

    const filteredTransactions = transactions.filter(tx => {
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            return (tx.description?.toLowerCase().includes(term)) ||
                   (tx.reference_no?.toLowerCase().includes(term)) ||
                   (tx.other_name?.toLowerCase().includes(term)) ||
                   (tx.partner_name?.toLowerCase().includes(term));
        }
        return true;
    });

    const getSourceTypeLabel = (type) => {
        switch(type) {
            case 'commission': return 'Commission Earned';
            case 'partner_transfer': return 'Partner Transfer';
            case 'customer_payment': return 'Customer Payment';
            case 'expense': return 'Expense';
            case 'salary': return 'Salary';
            default: return type || 'Other';
        }
    };

    const getSourceTypeIcon = (type) => {
        switch(type) {
            case 'commission': return faChartLine;
            case 'partner_transfer': return faExchangeAlt;
            case 'customer_payment': return faUser;
            default: return faMoneyBillWave;
        }
    };

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    // Calculate totals
    const totalCredits = transactions
        .filter(tx => tx.transaction_type === 'credit')
        .reduce((sum, tx) => sum + parseFloat(tx.amount || 0), 0);
    
    const totalDebits = transactions
        .filter(tx => tx.transaction_type === 'debit')
        .reduce((sum, tx) => sum + parseFloat(tx.amount || 0), 0);

    return (
        <div className={`owner-account-container ${isRTL ? 'rtl' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} />

            {/* Header */}
            <div className="account-header-modern">
                <div className="header-content">
                    <div className="header-icon">
                        <FontAwesomeIcon icon={faWallet} />
                    </div>
                    <div className="header-text">
                        <h1>Owner Account</h1>
                        <p>Track owner's USDT balance from partner transfers and commissions</p>
                        {currentShop && (
                            <div className="shop-badge">
                                <FontAwesomeIcon icon={faBuilding} /> {currentShop.shop_name}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Balance Cards */}
            <div className="balance-cards">
                <div className="balance-card total">
                    <div className="card-icon">
                        <FontAwesomeIcon icon={faWallet} />
                    </div>
                    <div className="card-content">
                        <div className="card-label">Total USDT Balance</div>
                        <div className="card-value">{formatCurrency(balance.total_balance)} USDT</div>
                        <div className="card-subvalue">Credits: +{formatCurrency(totalCredits)} | Debits: -{formatCurrency(totalDebits)}</div>
                    </div>
                </div>

                <div className="balance-card commission">
                    <div className="card-icon">
                        <FontAwesomeIcon icon={faChartLine} />
                    </div>
                    <div className="card-content">
                        <div className="card-label">Commission Earned</div>
                        <div className="card-value">{formatCurrency(balance.total_commission_earned)} USDT</div>
                        <div className="card-subvalue">From other account purchases</div>
                    </div>
                </div>

                <div className="balance-card transfers">
                    <div className="card-icon">
                        <FontAwesomeIcon icon={faExchangeAlt} />
                    </div>
                    <div className="card-content">
                        <div className="card-label">Partner Transfers</div>
                        <div className="card-value">{formatCurrency(balance.total_partner_transfers)} USDT</div>
                        <div className="card-subvalue">From partner account conversions</div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="filters-section">
                <div className="filter-buttons">
                    <button 
                        className={`filter-btn ${filterType === 'all' ? 'active' : ''}`}
                        onClick={() => setFilterType('all')}
                    >
                        All Transactions
                    </button>
                    <button 
                        className={`filter-btn ${filterType === 'commission' ? 'active' : ''}`}
                        onClick={() => setFilterType('commission')}
                    >
                        <FontAwesomeIcon icon={faPercent} style={{ marginRight: '4px' }} />
                        Commissions
                    </button>
                    <button 
                        className={`filter-btn ${filterType === 'partner_transfer' ? 'active' : ''}`}
                        onClick={() => setFilterType('partner_transfer')}
                    >
                        <FontAwesomeIcon icon={faExchangeAlt} style={{ marginRight: '4px' }} />
                        Partner Transfers
                    </button>
                </div>
                
                <div className="search-box">
                    <FontAwesomeIcon icon={faSearch} className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search by description, reference, or name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        dir={isRTL ? 'rtl' : 'ltr'}
                    />
                </div>
            </div>

            {/* Transactions Table */}
            <div className="transactions-section">
                <div className="section-header">
                    <h3>
                        <FontAwesomeIcon icon={faHistory} />
                        Transaction History
                        <span className="transaction-count">({filteredTransactions.length} transactions)</span>
                    </h3>
                    <button className="btn-print" onClick={() => window.print()}>
                        <FontAwesomeIcon icon={faPrint} /> Print
                    </button>
                </div>

                <div className="transactions-table-container">
                    {loading ? (
                        <div className="loading-state">
                            <div className="loading-spinner"></div>
                            <p>Loading transactions...</p>
                        </div>
                    ) : filteredTransactions.length === 0 ? (
                        <div className="empty-state">
                            <FontAwesomeIcon icon={faHistory} />
                            <p>No transactions found</p>
                        </div>
                    ) : (
                        <table className="transactions-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Type</th>
                                    <th>Source</th>
                                    <th>Description</th>
                                    <th className="text-right">Amount (USDT)</th>
                                    <th>Reference</th>
                                    <th>Commission Rate</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredTransactions.map(tx => (
                                    <tr key={tx.id} className={tx.transaction_type === 'credit' ? 'credit-row' : 'debit-row'}>
                                        <td>{formatDate(tx.transaction_date)}</td>
                                        <td>
                                            <span className={`type-badge ${tx.source_type}`}>
                                                <FontAwesomeIcon icon={getSourceTypeIcon(tx.source_type)} />
                                                {getSourceTypeLabel(tx.source_type)}
                                            </span>
                                        </td>
                                        <td>
                                            {tx.other_name && (
                                                <div className="source-info">
                                                    <FontAwesomeIcon icon={faUser} />
                                                    <span>{tx.other_name}</span>
                                                </div>
                                            )}
                                            {tx.partner_name && !tx.other_name && (
                                                <div className="source-info">
                                                    <FontAwesomeIcon icon={faBuilding} />
                                                    <span>{tx.partner_name}</span>
                                                </div>
                                            )}
                                            {!tx.other_name && !tx.partner_name && '-'}
                                        </td>
                                        <td>{tx.description || '-'}</td>
                                        <td className={`text-right ${tx.transaction_type === 'credit' ? 'credit-amount' : 'debit-amount'}`}>
                                            {tx.transaction_type === 'credit' ? '+' : '-'}{formatCurrency(tx.amount)} USDT
                                        </td>
                                        <td>{tx.reference_no || '-'}</td>
                                        <td>
                                            {tx.commission_rate > 0 && (
                                                <span className="commission-badge">
                                                    {tx.commission_rate}%
                                                </span>
                                            )}
                                            {(!tx.commission_rate || tx.commission_rate === 0) && '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="summary-row">
                                    <td colSpan="4" className="text-right"><strong>Total Credits:</strong></td>
                                    <td className="text-right credit-amount">
                                        <strong>+{formatCurrency(totalCredits)} USDT</strong>
                                    </td>
                                    <td colSpan="2"></td>
                                </tr>
                                <tr className="summary-row">
                                    <td colSpan="4" className="text-right"><strong>Total Debits:</strong></td>
                                    <td className="text-right debit-amount">
                                        <strong>-{formatCurrency(totalDebits)} USDT</strong>
                                    </td>
                                    <td colSpan="2"></td>
                                </tr>
                                <tr className="summary-row total-row">
                                    <td colSpan="4" className="text-right"><strong>Net Balance:</strong></td>
                                    <td className="text-right credit-amount">
                                        <strong>{formatCurrency(balance.total_balance)} USDT</strong>
                                    </td>
                                    <td colSpan="2"></td>
                                </tr>
                            </tfoot>
                        </table>
                    )}
                </div>
            </div>

            <style jsx>{`
                .owner-account-container {
                    padding: 20px;
                    background: #f5f6fa;
                    min-height: 100vh;
                }
                .account-header-modern {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    padding: 24px;
                    border-radius: 12px;
                    color: white;
                    margin-bottom: 24px;
                }
                .header-content {
                    display: flex;
                    align-items: center;
                    gap: 20px;
                }
                .header-icon {
                    width: 60px;
                    height: 60px;
                    background: rgba(255,255,255,0.2);
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 28px;
                }
                .header-text h1 {
                    margin: 0;
                    font-size: 24px;
                }
                .header-text p {
                    margin: 4px 0 0;
                    opacity: 0.9;
                }
                .shop-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    background: rgba(255,255,255,0.2);
                    padding: 4px 12px;
                    border-radius: 20px;
                    font-size: 12px;
                    margin-top: 8px;
                }
                .balance-cards {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
                    gap: 20px;
                    margin-bottom: 24px;
                }
                .balance-card {
                    background: white;
                    border-radius: 12px;
                    padding: 20px;
                    display: flex;
                    gap: 16px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
                }
                .balance-card.total {
                    border-top: 4px solid #667eea;
                }
                .balance-card.commission {
                    border-top: 4px solid #28a745;
                }
                .balance-card.transfers {
                    border-top: 4px solid #17a2b8;
                }
                .card-icon {
                    width: 50px;
                    height: 50px;
                    background: #f8f9fa;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 24px;
                }
                .balance-card.total .card-icon { color: #667eea; }
                .balance-card.commission .card-icon { color: #28a745; }
                .balance-card.transfers .card-icon { color: #17a2b8; }
                .card-content {
                    flex: 1;
                }
                .card-label {
                    font-size: 12px;
                    color: #888;
                    text-transform: uppercase;
                    margin-bottom: 4px;
                }
                .card-value {
                    font-size: 24px;
                    font-weight: 700;
                    color: #1a1a2e;
                }
                .card-subvalue {
                    font-size: 11px;
                    color: #999;
                    margin-top: 4px;
                }
                .filters-section {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 16px;
                    margin-bottom: 20px;
                    background: white;
                    padding: 16px;
                    border-radius: 10px;
                }
                .filter-buttons {
                    display: flex;
                    gap: 8px;
                }
                .filter-btn {
                    padding: 8px 16px;
                    border: 1px solid #ddd;
                    background: white;
                    border-radius: 6px;
                    cursor: pointer;
                    font-size: 13px;
                    transition: all 0.2s;
                }
                .filter-btn.active {
                    background: #667eea;
                    color: white;
                    border-color: #667eea;
                }
                .search-box {
                    position: relative;
                    width: 250px;
                }
                .search-box .search-icon {
                    position: absolute;
                    left: 10px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #999;
                    font-size: 12px;
                }
                .search-box input {
                    width: 100%;
                    padding: 8px 12px 8px 32px;
                    border: 1px solid #ddd;
                    border-radius: 6px;
                    font-size: 13px;
                    outline: none;
                }
                .transactions-section {
                    background: white;
                    border-radius: 12px;
                    padding: 20px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
                }
                .section-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 20px;
                    padding-bottom: 12px;
                    border-bottom: 2px solid #f0f0f0;
                }
                .section-header h3 {
                    margin: 0;
                    font-size: 16px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                .transaction-count {
                    font-size: 12px;
                    color: #888;
                    font-weight: normal;
                }
                .btn-print {
                    padding: 6px 12px;
                    background: #f8f9fa;
                    border: 1px solid #ddd;
                    border-radius: 6px;
                    cursor: pointer;
                    font-size: 12px;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }
                .transactions-table-container {
                    overflow-x: auto;
                }
                .transactions-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 13px;
                }
                .transactions-table th,
                .transactions-table td {
                    padding: 12px;
                    text-align: left;
                    border-bottom: 1px solid #f0f0f0;
                }
                .transactions-table th {
                    background: #f8f9fa;
                    font-weight: 600;
                    color: #555;
                }
                .text-right {
                    text-align: right !important;
                }
                .credit-row {
                    background: #f9fff9;
                }
                .debit-row {
                    background: #fff9f9;
                }
                .credit-amount {
                    color: #28a745;
                    font-weight: 600;
                }
                .debit-amount {
                    color: #dc3545;
                    font-weight: 600;
                }
                .type-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 4px 10px;
                    border-radius: 20px;
                    font-size: 11px;
                    font-weight: 600;
                }
                .type-badge.commission {
                    background: #e8f5e9;
                    color: #28a745;
                }
                .type-badge.partner_transfer {
                    background: #e3f2fd;
                    color: #17a2b8;
                }
                .commission-badge {
                    display: inline-block;
                    padding: 2px 8px;
                    background: #fff3e0;
                    color: #ff9800;
                    border-radius: 12px;
                    font-size: 11px;
                    font-weight: 600;
                }
                .source-info {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    font-size: 12px;
                }
                .summary-row td {
                    border-top: 2px solid #e9ecef;
                    background: #f8f9fa;
                    font-weight: 600;
                }
                .total-row td {
                    background: #e9ecef;
                    font-size: 14px;
                }
                .loading-state, .empty-state {
                    text-align: center;
                    padding: 60px;
                    color: #999;
                }
                .loading-spinner {
                    width: 40px;
                    height: 40px;
                    border: 3px solid #e9ecef;
                    border-top-color: #667eea;
                    border-radius: 50%;
                    animation: spin 0.8s linear infinite;
                    margin: 0 auto 12px;
                }
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
                .toast-notification {
                    position: fixed;
                    top: 20px;
                    right: 20px;
                    z-index: 9999;
                    animation: slideIn 0.3s ease;
                }
                @keyframes slideIn {
                    from { transform: translateX(110%); opacity: 0; }
                    to { transform: translateX(0); opacity: 1; }
                }
                .toast-content {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 14px 20px;
                    color: white;
                    border-radius: 10px;
                    box-shadow: 0 4px 16px rgba(0,0,0,0.2);
                }
                .toast-notification.success .toast-content {
                    background: #28a745;
                }
                .toast-notification.error .toast-content {
                    background: #dc3545;
                }
                .rtl .text-right {
                    text-align: left !important;
                }
                .rtl .search-box .search-icon {
                    left: auto;
                    right: 10px;
                }
                .rtl .search-box input {
                    padding: 8px 32px 8px 12px;
                }
                @media print {
                    .filters-section, .btn-print {
                        display: none;
                    }
                    .owner-account-container {
                        background: white;
                        padding: 0;
                    }
                }
            `}</style>
        </div>
    );
};

export default OwnerAccount;